/**
 * MoonStartTreatmentModal
 *
 * Treatment setup form matching Moon's modal pattern (AddAppointmentModal style).
 */

import { useState, useCallback, useEffect } from "react";
import {
  View,
  Pressable,
  TextInput,
  ScrollView,
  Text,
  Platform,
} from "react-native";
import { Building2, Check, ChevronLeft } from "lucide-react-native";
import {
  Dialog,
  DialogIcon,
  DialogPrimaryButton,
  MoonCheckbox,
  SoftSecondaryButton,
  MoonDateField,
  MoonFieldInput,
  MoonFormField,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { EMBER } from "../../../../onboarding/theme";
import { toLocalDateString } from "@src/packages/utils/date";
import type { CycleType as ApiCycleType } from "@src/packages/cycles/hooks/useCycles";
import {
  getFrozenSpecimenInitialTreatment,
  getFrozenSpecimenTreatmentTargets,
} from "@src/packages/care/cryovault/frozenSpecimenTreatmentTarget";
import {
  SelectFrozenSpecimensModal,
  type FrozenSpecimenSelection,
} from "./SelectFrozenSpecimensModal";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";

const TREATMENT_SCHEDULE_ICON =
  require("../../../../assets/images/treatment-schedule.png") as number;

export type CycleType = ApiCycleType;

export type StartTreatmentFormData = {
  treatmentType: CycleType;
  clinicName: string;
  startDate: Date | null;
  hasDecisionAndConsent: boolean;
  frozenSelection?: FrozenPrefill | null;
};

export type EditTreatmentData = {
  cycleType: CycleType;
  clinicName: string;
  startDate: Date | null;
};

export type FrozenPrefill = {
  type: "semen" | "oocyte" | "embryo";
  specimenIds: string[];
  specimenLabels?: string[];
};

type Props = {
  visible: boolean;
  onClose: () => void;
  onStartTreatment: (data: StartTreatmentFormData) => void;
  isLoading?: boolean;
  goal?: "preserve" | "pregnancy";
  gender?: "male" | "female" | null;
  editData?: EditTreatmentData | null;
  /** When editing, jump straight to the "where & when" step. */
  initialStep?: 1 | 2;
  /** Keep scoped cycle-detail shortcuts on the where-and-when step. */
  allowTreatmentTypeBackNavigation?: boolean;
  /** Pre-selected cycle type derived from the user's intent. */
  presetCycleType?: CycleType | null;
  /** Pre-fill from a CryoVault flow. */
  frozenPrefill?: FrozenPrefill | null;
  /** Preserve a partially-created consuming cycle while its reservation is retried. */
  frozenSelectionCycleId?: string | null;
};

const ALL_CYCLE_ORDER: CycleType[] = [
  "ivf",
  "icsi",
  "iui",
  "egg_freezing",
  "sperm_freezing",
  "embryo_freezing",
  "fet",
  "oi",
];

const FEMALE_LABEL_KEY: Record<CycleType, string> = {
  ivf: "care.start_treatment.cycle_ivf",
  icsi: "care.start_treatment.cycle_icsi",
  iui: "care.start_treatment.cycle_iui",
  fet: "care.start_treatment.cycle_fet",
  oi: "care.start_treatment.cycle_oi",
  egg_freezing: "care.start_treatment.cycle_egg_freezing",
  embryo_freezing: "care.start_treatment.cycle_embryo_freezing",
  sperm_freezing: "care.start_treatment.cycle_sperm_freezing",
};

const MALE_LABEL_KEY: Record<CycleType, string> = {
  ivf: "care.start_treatment.cycle_ivf_partner",
  icsi: "care.start_treatment.cycle_icsi_partner",
  iui: "care.start_treatment.cycle_iui_partner",
  fet: "care.start_treatment.cycle_fet_partner",
  oi: "care.start_treatment.cycle_oi",
  egg_freezing: "care.start_treatment.cycle_egg_freezing",
  embryo_freezing: "care.start_treatment.cycle_embryo_freezing_partner",
  sperm_freezing: "care.start_treatment.cycle_sperm_freezing",
};

function resolveTreatmentLabelKey(
  labelKeyMap: Record<CycleType, string>,
  treatmentType: CycleType,
): string {
  return labelKeyMap[treatmentType] ?? "care.start_treatment.cycle_unavailable";
}

function CycleTypeList({
  value,
  onChange,
  options,
}: {
  value: CycleType | null;
  onChange: (value: CycleType) => void;
  options: { value: CycleType; label: string }[];
}) {
  const capturePress = usePressCapture("CycleTypeList");
  return (
    <View style={{ gap: 12 }}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Pressable
            key={option.value}
            onPress={() => {
              capturePress(pressLabel(option.label), "cycle_type_option");
              onChange(option.value);
            }}
            accessibilityRole="button"
            accessibilityLabel={option.label}
            accessibilityState={{ selected }}
            aria-selected={selected}
            className="flex-row items-center rounded-[10px] px-[14px] py-3"
            style={({ pressed }) => ({
              gap: 10,
              borderWidth: 1,
              borderColor: selected ? "rgba(59,26,8,0.18)" : "rgba(0,0,0,0.06)",
              backgroundColor: selected
                ? "rgba(59,26,8,0.06)"
                : pressed
                  ? "rgba(0,0,0,0.03)"
                  : "transparent",
            })}
          >
            <Text
              className="font-dm-sans"
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: selected ? "600" : "400",
                color: EMBER.colors.text,
              }}
            >
              {option.label}
            </Text>
            {selected && (
              <Check size={16} color={EMBER.colors.text} strokeWidth={2} />
            )}
          </Pressable>
        );
      })}
    </View>
  );
}

export function MoonStartTreatmentModal({
  visible,
  onClose,
  onStartTreatment,
  isLoading = false,
  gender = null,
  editData,
  initialStep = 1,
  allowTreatmentTypeBackNavigation = true,
  presetCycleType = null,
  frozenPrefill,
  frozenSelectionCycleId = null,
}: Props) {
  const t = useTranslations();
  const capturePress = usePressCapture("MoonStartTreatmentModal");
  const isEditMode = !!editData;
  const [selectedFrozenPrefill, setSelectedFrozenPrefill] =
    useState<FrozenPrefill | null>(frozenPrefill ?? null);
  const [selectionModalVisible, setSelectionModalVisible] = useState(false);
  const activeFrozenPrefill = selectedFrozenPrefill;

  const labelKeyMap = gender === "male" ? MALE_LABEL_KEY : FEMALE_LABEL_KEY;
  const cycleOrder = activeFrozenPrefill
    ? getFrozenSpecimenTreatmentTargets(activeFrozenPrefill.type)
    : ALL_CYCLE_ORDER;
  const treatmentOptions = cycleOrder.map((value) => ({
    value,
    label: t(resolveTreatmentLabelKey(labelKeyMap, value)),
  }));

  // Broad preservation intent is not enough to choose eggs, sperm, or
  // embryos. The caller must provide an explicit preset.
  const presetType: CycleType | null = presetCycleType ?? null;

  const [step, setStep] = useState<1 | 2>(initialStep);
  const [treatmentType, setTreatmentType] = useState<CycleType | null>(
    editData?.cycleType ?? presetType,
  );
  const [clinicName, setClinicName] = useState(editData?.clinicName ?? "");
  const [dateText, setDateText] = useState("");
  const [hasDecisionAndConsent, setHasDecisionAndConsent] = useState(false);
  const hasFrozenSelection = Boolean(activeFrozenPrefill?.specimenIds.length);

  useEffect(() => {
    if (!visible) return;
    setStep(initialStep);
    setTreatmentType(editData?.cycleType ?? presetType);
    setHasDecisionAndConsent(false);
    setSelectedFrozenPrefill(frozenPrefill ?? null);
    setSelectionModalVisible(false);
  }, [visible, initialStep, editData?.cycleType, frozenPrefill, presetType]);

  useEffect(() => {
    if (!visible || !editData) return;
    setTreatmentType(editData.cycleType);
    setClinicName(editData.clinicName);
    setDateText(
      editData.startDate ? toLocalDateString(editData.startDate) : "",
    );
    setStep(initialStep);
  }, [visible, editData, initialStep]);

  useEffect(() => {
    if (!visible || !activeFrozenPrefill) return;
    setTreatmentType(
      getFrozenSpecimenInitialTreatment(activeFrozenPrefill.type),
    );
  }, [visible, activeFrozenPrefill]);

  /**
   * Closes even mid-submit. Refusing to close while a write was in flight left
   * a request the server never answered with no exit at all — the close button
   * and the backdrop were both refused, and only a reload got the patient out.
   *
   * The backdrop stays inert while loading (`dismissOnBackdropTap`) so a stray
   * tap cannot abandon a submit that is about to land; leaving deliberately
   * through the close button is what this restores.
   */
  const handleClose = useCallback(() => {
    setTreatmentType(editData?.cycleType ?? presetType);
    setClinicName(editData?.clinicName ?? "");
    setDateText("");
    setHasDecisionAndConsent(false);
    setSelectedFrozenPrefill(frozenPrefill ?? null);
    setSelectionModalVisible(false);
    setStep(1);
    onClose();
  }, [editData, frozenPrefill, onClose, presetType]);

  const handleFrozenSelectionConfirm = useCallback(
    (selection: FrozenSpecimenSelection) => {
      setSelectedFrozenPrefill((current) =>
        current
          ? {
              ...current,
              specimenIds: selection.specimenIds,
              specimenLabels: undefined,
            }
          : current,
      );
      setHasDecisionAndConsent(false);
      setSelectionModalVisible(false);
    },
    [],
  );

  const canAdvance = !!treatmentType;
  const handleNext = useCallback(() => {
    if (canAdvance) setStep(2);
  }, [canAdvance]);
  const handleBack = useCallback(() => setStep(1), []);
  const canGoBackToTreatmentType =
    step === 2 && allowTreatmentTypeBackNavigation;
  const isScopedWhereWhenEdit =
    step === 2 && isEditMode && !allowTreatmentTypeBackNavigation;
  const secondaryActionFlex = step === 2 ? 0.9 : 1;
  const primaryActionFlex = step === 2 ? 1.1 : 1;
  const handleSubmit = useCallback(() => {
    if (!treatmentType || (hasFrozenSelection && !hasDecisionAndConsent)) {
      return;
    }
    onStartTreatment({
      treatmentType,
      clinicName,
      startDate: dateText ? new Date(dateText + "T00:00:00") : null,
      hasDecisionAndConsent: hasFrozenSelection ? hasDecisionAndConsent : false,
      frozenSelection: activeFrozenPrefill,
    });
  }, [
    activeFrozenPrefill,
    clinicName,
    dateText,
    hasDecisionAndConsent,
    hasFrozenSelection,
    onStartTreatment,
    treatmentType,
  ]);

  const treatmentLabel = treatmentType
    ? t(resolveTreatmentLabelKey(labelKeyMap, treatmentType))
    : "";
  return (
    <Dialog
      visible={visible}
      onClose={handleClose}
      layout="centered"
      closeButton="minimal"
      dismissOnBackdropTap={!isLoading}
      testID="start-treatment-modal"
    >
      {canGoBackToTreatmentType && (
        <Pressable
          onPress={() => {
            capturePress("back");
            handleBack();
          }}
          hitSlop={8}
          style={{
            position: "absolute",
            top: 12,
            left: 12,
            width: 28,
            height: 28,
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <ChevronLeft
            size={20}
            color={EMBER.colors.textLight}
            strokeWidth={2}
          />
        </Pressable>
      )}

      <DialogIcon
        source={TREATMENT_SCHEDULE_ICON}
        width={72}
        height={72}
        style={{ marginBottom: 14 }}
      />
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 18,
          fontWeight: "600",
          color: EMBER.colors.text,
          marginBottom: 6,
          textAlign: "center",
        }}
      >
        {step === 1
          ? isEditMode
            ? t("care.start_treatment.title_edit")
            : t("care.start_treatment.title_start")
          : t("care.start_treatment.title_where_when")}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          fontSize: 13,
          color: EMBER.colors.textLight,
          lineHeight: 18,
          marginBottom: 14,
          textAlign: "center",
        }}
      >
        {step === 1
          ? t("care.start_treatment.subtitle_step1")
          : t("care.start_treatment.subtitle_step2", {
              params: { label: treatmentLabel },
            })}
      </Text>

      {step === 1 ? (
        <ScrollView
          style={{ maxHeight: 320 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <CycleTypeList
            value={treatmentType}
            onChange={setTreatmentType}
            options={treatmentOptions}
          />
        </ScrollView>
      ) : (
        <ScrollView
          style={{ maxHeight: 360 }}
          showsVerticalScrollIndicator={false}
          keyboardShouldPersistTaps="handled"
        >
          <MoonFormField label={t("care.start_treatment.clinic_label")}>
            <MoonFieldInput paddingY={2}>
              <View
                style={{
                  flexDirection: "row",
                  alignItems: "center",
                  gap: 10,
                }}
              >
                <Building2
                  size={13}
                  color={EMBER.colors.textLight}
                  strokeWidth={1.5}
                />
                <TextInput
                  value={clinicName}
                  onChangeText={setClinicName}
                  placeholder={t("care.start_treatment.clinic_placeholder")}
                  placeholderTextColor={EMBER.colors.textFaint}
                  autoComplete="off"
                  autoCorrect={false}
                  spellCheck={false}
                  {...(Platform.OS === "web" && {
                    autoCapitalize: "none" as const,
                    textContentType: "none" as const,
                    role: "presentation" as const,
                  })}
                  style={{
                    flex: 1,
                    paddingVertical: 10,
                    fontSize: 14,
                    lineHeight: 20,
                    color: EMBER.colors.text,
                    fontFamily: "DMSans_400Regular",
                    ...(Platform.OS === "web"
                      ? { outlineStyle: "none" as const }
                      : {}),
                  }}
                />
              </View>
            </MoonFieldInput>
          </MoonFormField>

          <View style={{ marginTop: 18 }}>
            <MoonDateField
              label={t("care.start_treatment.date_label")}
              value={dateText}
              onChange={setDateText}
              hint={t("care.start_treatment.date_hint")}
            />
          </View>

          {activeFrozenPrefill && activeFrozenPrefill.specimenIds.length > 0 ? (
            <View
              style={{
                marginTop: 18,
                padding: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: "rgba(59,26,8,0.12)",
                backgroundColor: "rgba(59,26,8,0.05)",
                gap: 4,
              }}
            >
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  fontWeight: "700",
                  color: EMBER.colors.text,
                }}
              >
                {t("care.start_treatment.frozen_selection_title")}
              </Text>
              <Text
                className="font-dm-sans"
                style={{
                  fontSize: 12,
                  lineHeight: 17,
                  color: EMBER.colors.textLight,
                }}
              >
                {t("care.start_treatment.frozen_selection_summary", {
                  params: {
                    count: String(activeFrozenPrefill.specimenIds.length),
                  },
                })}
              </Text>
              {activeFrozenPrefill.specimenLabels?.map((label, index) => (
                <Text
                  key={`${label}-${index}`}
                  className="font-dm-sans"
                  style={{
                    fontSize: 12,
                    lineHeight: 17,
                    color: EMBER.colors.text,
                  }}
                >
                  · {label}
                </Text>
              ))}
              {activeFrozenPrefill.type !== "semen" ? (
                <Pressable
                  accessibilityRole="button"
                  onPress={() => {
                    capturePress(
                      pressLabel(
                        t("care.start_treatment.change_frozen_selection"),
                      ),
                      "care.start_treatment.change_frozen_selection",
                    );
                    setSelectionModalVisible(true);
                  }}
                  style={{ alignSelf: "flex-start", marginTop: 6 }}
                >
                  <Text
                    className="font-dm-sans"
                    style={{
                      color: EMBER.colors.warm,
                      fontSize: 12,
                      fontWeight: "700",
                    }}
                  >
                    {t("care.start_treatment.change_frozen_selection")}
                  </Text>
                </Pressable>
              ) : null}
              <MoonCheckbox
                value={hasDecisionAndConsent}
                onChange={setHasDecisionAndConsent}
                label={t(
                  "care.start_treatment.frozen_selection_authority_confirmation",
                )}
                accentColor={EMBER.colors.warm}
                style={{ marginTop: 8 }}
                testID="frozen-selection-consent"
              />
            </View>
          ) : null}
        </ScrollView>
      )}

      <View
        testID="start-treatment-actions"
        style={{ flexDirection: "row", gap: 12, marginTop: 20 }}
      >
        <SoftSecondaryButton
          label={
            canGoBackToTreatmentType
              ? t("care.start_treatment.back")
              : t("care.start_treatment.cancel")
          }
          onPress={canGoBackToTreatmentType ? handleBack : handleClose}
          flex={secondaryActionFlex}
        />
        {step === 1 ? (
          <DialogPrimaryButton
            label={t("care.start_treatment.next")}
            onPress={handleNext}
            disabled={!canAdvance}
            flex={primaryActionFlex}
          />
        ) : (
          <DialogPrimaryButton
            label={
              isScopedWhereWhenEdit
                ? t("care.start_treatment.save")
                : isEditMode
                  ? t("care.start_treatment.update_cycle")
                  : t("care.start_treatment.start_cycle")
            }
            onPress={handleSubmit}
            disabled={hasFrozenSelection && !hasDecisionAndConsent}
            loading={isLoading}
            flex={primaryActionFlex}
          />
        )}
      </View>
      <SelectFrozenSpecimensModal
        visible={selectionModalVisible}
        type={
          activeFrozenPrefill?.type === "oocyte" ||
          activeFrozenPrefill?.type === "embryo"
            ? activeFrozenPrefill.type
            : null
        }
        initialSpecimenIds={activeFrozenPrefill?.specimenIds}
        onBack={() => setSelectionModalVisible(false)}
        onConfirm={handleFrozenSelectionConfirm}
        allowedCycleId={frozenSelectionCycleId}
      />
    </Dialog>
  );
}
